import React from 'react';
import { ProductCard } from '../components/ProductCard';
import { useProducts } from '../context/ProductContext';
import { PageHero } from '../components/PageHero';
import { SEO } from '../components/SEO';
import { Loader } from 'lucide-react';

export const Shop = () => {
  const { products, loading } = useProducts();
  const [category, setCategory] = React.useState("All");
  const [sortBy, setSortBy] = React.useState("featured");
  const [priceRange, setPriceRange] = React.useState(200);

  const categories = ["All", "Sexual Wellness", "Detox Programs", "Herbal Supplements", "Stress Relief"];

  const filteredProducts = React.useMemo(() => {
    let result = [...products];

    // Filter by category
    if (category !== "All") {
      result = result.filter(p => p.tag?.includes(category) || p.description.includes(category));
    }

    // Filter by Price
    result = result.filter(p => p.price <= priceRange);

    // Sort
    if (sortBy === "price-asc") {
      result.sort((a, b) => a.price - b.price);
    } else if (sortBy === "price-desc") {
      result.sort((a, b) => b.price - a.price);
    }
    // "featured" could be default order or specific logic

    return result;
  }, [products, category, sortBy, priceRange]);

  const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.simbaherbals.com';
  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Simba Herbals UK – Natural Remedies',
    description: 'Potent formulations for vitality and strength. Congo Dust, VigPower, and the Mufasa Protocol.',
    url: `${SITE_URL}/shop`,
    numberOfItems: products.length,
    itemListElement: products.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: p.name,
      url: `${SITE_URL}/product/${p.slug}`,
      image: p.image.startsWith('http') ? p.image : `${SITE_URL}${p.image}`,
    })),
  };

  return (
    <div className="pb-20 bg-gray-50/50">
      <SEO
        title="Shop Natural Remedies"
        description="Potent formulations for vitality and strength. Browse Simba Herbals UK natural men's wellness products—Congo Dust, VigPower, and the Mufasa Protocol."
        canonical="/shop"
        jsonLd={itemListJsonLd}
      />
      <PageHero 
        title="Shop Natural Remedies" 
        subtitle="Potent formulations for checking vitality and strength."
        image="https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&q=80&w=2000"
        height="h-[60vh] lg:h-[70vh]"
      />
      
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-12">
        
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Left Sidebar Filters */}
          <div className="lg:w-64 flex-shrink-0 space-y-8 mt-12">
            <div>
              <h3 className="font-display font-bold text-lg mb-4 text-simba-dark">Categories</h3>
              <div className="flex flex-col space-y-2">
                {categories.map((cat) => (
                  <button 
                    key={cat}
                    onClick={() => setCategory(cat)}
                    className={`text-left px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                      category === cat 
                        ? 'bg-simba-green text-white shadow-lg shadow-simba-green/20' 
                        : 'bg-white text-gray-600 hover:bg-simba-green/5 hover:text-simba-green'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Price Range Filter */}
            <div>
               <h3 className="font-display font-bold text-lg mb-4 text-simba-dark">Price Range</h3>
               <div className="bg-white p-6 rounded-xl border border-gray-100 space-y-4 shadow-sm">
                  <div className="flex items-center justify-between text-sm font-bold text-simba-dark mb-2">
                     <span>£0</span>
                     <span>£{priceRange}</span>
                  </div>
                  <input 
                    type="range" 
                    min="0" 
                    max="200" 
                    value={priceRange} 
                    onChange={(e) => setPriceRange(Number(e.target.value))}
                    className="w-full h-2 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-simba-green"
                  />
                  <div className="text-xs text-gray-400 text-center pt-2">
                    Max Price: £{priceRange}
                  </div>
               </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1">
            {/* Sort Header */}
            <div className="flex justify-between items-center mb-8 pb-6 border-b border-gray-200">
              <span className="text-gray-500 font-medium">{filteredProducts.length} Results</span>
              
              <div className="flex items-center gap-3">
                 <span className="text-sm font-medium text-gray-500 hidden sm:inline">Sort by:</span>
                 <select 
                   value={sortBy}
                   onChange={(e) => setSortBy(e.target.value)}
                   className="bg-white border border-gray-200 text-simba-dark text-sm rounded-lg focus:ring-simba-green focus:border-simba-green block p-2.5 font-bold cursor-pointer hover:border-gray-300 transition-colors"
                 >
                   <option value="featured">Featured</option>
                   <option value="price-asc">Price: Low to High</option>
                   <option value="price-desc">Price: High to Low</option>
                 </select>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-32 text-simba-green">
                 <Loader className="animate-spin mr-2" /> <span className="font-bold">Loading Mufasa Energy...</span>
              </div>
            ) : (
              <>
                {filteredProducts.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredProducts.map((product) => (
                      <ProductCard 
                        key={product.id}
                        {...product}
                      />
                    ))}
                  </div>
                ) : (
                   <div className="text-center py-20 bg-white rounded-3xl border border-gray-100 shadow-sm">
                      <p className="text-xl text-gray-400 font-display mb-2">No products found</p>
                      <p className="text-gray-500">Try adjusting your filters to find what you're looking for.</p>
                      <button 
                        onClick={() => {setCategory("All"); setPriceRange(200); setSortBy("featured");}}
                        className="mt-6 text-simba-green font-bold hover:underline"
                      >
                        Clear Filters
                      </button>
                   </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
